import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { IconButton } from '@/components/atoms/IconButton';
import styles from './Dialog.module.scss';
import type {
  DialogProps,
  DialogTriggerProps,
  DialogPortalProps,
  DialogOverlayProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogFooterProps,
  DialogTitleProps,
  DialogDescriptionProps,
  DialogOverlayRef,
  DialogContentRef,
} from './Dialog.types';

export const Dialog: React.FC<DialogProps> = DialogPrimitive.Root;

export const DialogTrigger: React.FC<DialogTriggerProps> = DialogPrimitive.Trigger;

export const DialogPortal: React.FC<DialogPortalProps> = DialogPrimitive.Portal;

export const DialogClose: React.FC<DialogPrimitive.DialogCloseProps> = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<DialogOverlayRef, DialogOverlayProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(styles.dialog__overlay, className)}
      {...props}
    />
  )
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DialogContent = React.forwardRef<DialogContentRef, DialogContentProps>(
  (
    { className, children, variant = 'default', size = 'md', hideDefaultClose = false, ...props },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          styles.dialog__content,
          styles[`dialog__content--${size}`],
          variant === 'fullscreen' && styles['dialog__content--fullscreen'],
          className
        )}
        {...props}
      >
        {children}
        {!hideDefaultClose && (
          <DialogPrimitive.Close asChild>
            <IconButton icon={X} aria-label="Close" className={styles.dialog__close} />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DialogHeader: React.FC<DialogHeaderProps> = ({ className, ...props }) => (
  <div className={cn(styles.dialog__header, className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

export const DialogFooter: React.FC<DialogFooterProps> = ({ className, ...props }) => (
  <div className={cn(styles.dialog__footer, className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(styles.dialog__title, className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(styles.dialog__description, className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
